import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageMeta } from "@/components/seo/PageMeta";
import { ArticleGridSkeleton } from "@/components/news/ArticleGridSkeleton";
import { ArticleCard } from "@/components/news/ArticleCard";
import { Latest48hModule } from "@/components/news/Latest48hModule";
import { ClientBannerCarousel } from "@/components/portal/ClientBannerCarousel";
import { AdvertisePartnerCta, MostViewedPanel } from "@/components/portal/PortalSidebar";
import { resolveCategoryColor } from "@/lib/categoryColors";
import { selectFreshFeed } from "@/lib/freshness";
import {
  fetchArticles,
  fetchCategories,
  fetchCategoryArticles,
  fetchSiteSettings,
} from "@/services/news";

export function HomePage() {
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSiteSettings });

  const { data: featuredData, isLoading: loadingFeatured } = useQuery({
    queryKey: ["articles", "featured"],
    queryFn: () => fetchArticles({ featured: "true", ordering: "-publish_at" }),
  });

  const { data: latestData, isLoading: loadingLatest } = useQuery({
    queryKey: ["articles", "latest"],
    queryFn: () => fetchArticles({ ordering: "-publish_at" }),
  });

  const { data: mostViewedData, isLoading: loadingMostViewed } = useQuery({
    queryKey: ["articles", "most-viewed"],
    queryFn: () => fetchArticles({ ordering: "-views" }),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const sectionCategories = categories.filter((c) => (c.article_count || 0) > 0).slice(0, 6);

  const categoryQueries = useQueries({
    queries: sectionCategories.map((category) => ({
      queryKey: ["category-articles", category.slug, "home-6"],
      queryFn: () =>
        fetchCategoryArticles(category.slug, { ordering: "-publish_at", page_size: "6" }),
    })),
  });

  const freshFeed = useMemo(
    () =>
      selectFreshFeed(
        [latestData?.results || [], featuredData?.results || []],
        4,
      ),
    [latestData?.results, featuredData?.results],
  );

  const mostViewed = mostViewedData?.results?.slice(0, 4) || latestData?.results?.slice(0, 4) || [];
  const loadingHero = loadingFeatured || loadingLatest;
  const loadingSidebar = loadingMostViewed && loadingLatest;

  return (
    <>
      <PageMeta description={site?.tagline} />

      <div className="portal-grid">
        <div className="portal-main space-y-7">
          <ClientBannerCarousel placement="home_featured" variant="featured" />

          <Latest48hModule articles={freshFeed} loading={loadingHero} />

          {sectionCategories.map((category, index) => {
            const query = categoryQueries[index];
            const articles = query.data?.results || [];
            if (!articles.length && !query.isLoading) return null;

            const accentColor = resolveCategoryColor(category.slug, category.color);
            // Grid 3 colunas → até 6 cards (2 linhas completas)
            const items = articles.slice(0, 6);
            const count = items.length;

            return (
              <section key={category.id} className="space-y-3">
                <div className="flex items-center justify-between gap-3 border-b border-brand-outline-variant/40 pb-2">
                  <h2
                    className="section-accent mb-0"
                    style={{ borderColor: accentColor, color: accentColor }}
                  >
                    {category.name}
                  </h2>
                  <Link
                    to={`/categoria/${category.slug}`}
                    className="shrink-0 text-sm font-semibold text-brand-primary hover:underline"
                  >
                    Ver todas
                  </Link>
                </div>

                {query.isLoading ? (
                  <ArticleGridSkeleton count={Math.min(6, category.article_count || 3)} />
                ) : count === 1 ? (
                  <ArticleCard article={items[0]} horizontal priority={index === 0} />
                ) : count === 2 ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {items.map((article, articleIndex) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        priority={index === 0 && articleIndex === 0}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((article, articleIndex) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        featured={articleIndex === 0}
                        priority={index === 0 && articleIndex === 0}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <aside className="portal-sidebar space-y-6">
          <ClientBannerCarousel placement="home_sidebar" variant="sidebar" />
          {loadingSidebar ? (
            <div
              className="min-h-[22rem] animate-pulse rounded border border-brand-outline-variant/40 bg-brand-surface-container"
              aria-hidden
            />
          ) : mostViewed.length ? (
            <MostViewedPanel articles={mostViewed} />
          ) : null}
          <AdvertisePartnerCta />
        </aside>
      </div>
    </>
  );
}
